import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { IconPlayerPlay, IconPlus } from '@tabler/icons-react';

import Sidebar from '../../components/Sidebar/Sidebar';
import PageHeader from '../../components/PageHeader/PageHeader';
import TutorialDialog from '../../components/TutorialDialog/TutorialDialog';
import ProjectListHeader, { ViewType } from '../../components/ProjectListHeader/ProjectListHeader';
import styles from './DashboardPage.module.scss';
import ProjectList, {type ProjectListProject} from '../../components/ProjectList/ProjectList';
import CreateProjectPopup from '../../components/CreateProjectPopup/CreateProjectPopup';
import CreateTemplatePopup from '../../components/CreateTemplatePopup/CreateTemplatePopup';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import NotificationBell from '../../components/NotificationBell/NotificationBell';
import SystemAdminBadge from '../../components/SystemAdminBadge/SystemAdminBadge';
import { getProject, useDeleteProjectId } from '../../api/project-api/project-api';
import type { Status, ProjectType } from '../../api/model';
import { PermissionKey } from '../../api/model';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { getApiErrorMessage } from '../../hooks/useApiMutation';

// Import project logo
import QIcon from '../../assets/images/QIcon.svg';
import PortfolioTiles from '../../components/DashboardCards/PortfolioTiles';
import { usePortfolioIntelligence } from '../../hooks/usePortfolioIntelligence';
import {
  oldestComputedAt,
  portfolioRows,
  portfolioRowsById,
  portfolioSummary,
} from '../../components/ReportMetrics/portfolio';

const DashboardPage: React.FC = () => {
  // Load saved view preference
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    const savedView = localStorage.getItem('projectListView');
    return (savedView === 'list' || savedView === 'card') ? savedView : 'list';
  });
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
    const [templateSourceProject, setTemplateSourceProject] = useState<{
        id: string;
        title: string;
        description?: string;
    } | null>(null);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
    const {can, isSystemAdmin} = useCurrentUserPermissions();
  const canCreateProject = can(PermissionKey.CREATE_PROJECT, 'CREATE');
    const canSaveTemplates = !isSystemAdmin && can(PermissionKey.SAVE_TEMPLATES, 'CREATE');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { t } = useTranslation('dashboard');

  // delete project mutationn
  const deleteProjectMutation = useDeleteProjectId(undefined, queryClient);

  React.useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['projects'] });
  }, [queryClient]);
  
  // Save view pref
  React.useEffect(() => {
    localStorage.setItem('projectListView', currentView);
  }, [currentView]);

  // Fetch all projects
  const {
    data: projects = [],
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProject(),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 2,
  });

  // The KPI engine's view of the same projects (spec §17's Dashboard, p. 35).
  // Loaded separately and joined by id rather than folded into /project, so a
  // slow or failed intelligence read never delays or blanks the project list -
  // every column built from it just falls back to a dash.
  const { projects: portfolio } = usePortfolioIntelligence();
  const intelligenceById = React.useMemo(() => portfolioRowsById(portfolio), [portfolio]);
  const portfolioViewRows = React.useMemo(() => portfolioRows(portfolio), [portfolio]);
  const portfolioBand = React.useMemo(() => portfolioSummary(portfolioViewRows), [portfolioViewRows]);
  // the stalest project, since that is how current this page really is
  const portfolioComputedAt = React.useMemo(
    () => oldestComputedAt(portfolioViewRows),
    [portfolioViewRows],
  );

  // Helper functions for data transformation
  const mapApiStatusToUI = (status: Status | undefined) => {
    switch (status) {
      case 'PLANNED': return 'In Progress' as const;
      case 'IN_PROGRESS': return 'In Progress' as const;
      case 'FINISHED': return 'Completed' as const;
      case 'CANCELLED': return 'Place holder' as const;
      case 'DELAYED': return 'Delayed' as const;
      default: return 'Place holder' as const;
    }
  };

  const formatDate = (date: string | undefined): string => {
    if (!date) return new Date().toISOString();
    return new Date(date).toISOString();
  };

  // Apı projects to ui format - if only valid id
  const uiProjects = projects
    .filter(project => project.id != null && project.id !== undefined) 
    .map((project) => {
      const intelligence = intelligenceById.get(project.id!);
      return {
      id: project.id!.toString(),
      title: project.name || 'Untitled Project',
      description: `Project ${project.id}`,
      logoUrl: project.icon || QIcon,
      type: (project.projectType || 'PROJECT') as ProjectType,
      method: project.method as 'AGILE' | 'CLASSIC' | 'HYBRID' | undefined,
      status: mapApiStatusToUI(project.status),
      teamMembers: (project.projectCharacters || []).map((characterName, idx) => ({
        id: `char-${idx}`,
        name: characterName,
        avatarUrl: ''
      })),
      lastEdit: {
        date: formatDate(project.modified?.toString()),
        editor: 'System'
      },
      // op.project_progress is the engine's weighted figure; Project.progress is
      // a free-settable field that the engine does not maintain. Preferring the
      // engine here is the same fix the Gantt got (spec §4): two places showing
      // different percentages for one project is worse than either number.
      progress: intelligence?.progress != null
        ? Math.round(intelligence.progress)
        : project.progress ?? 0,
      startDate: project.startTime?.toString(),
      endDate: project.endTime?.toString(),
      intelligence,
      };
    });

  const handleProjectClick = (project: { id: string | number }) => {
    // Ensure projectId is a string for navigation
    const projectId = String(project.id);
    
    // validation
    const numericId = Number(projectId);
    if (Number.isNaN(numericId) || numericId <= 0) {
      console.error('Invalid project ID for navigation:', projectId);
      showError('Invalid Project ID', `Cannot navigate to project with ID: ${projectId}`);
      return;
    }
    navigate({ to: '/project/$projectId', params: { projectId }, search: { tab: 'overview' } });
  };

  const handleProjectDelete = async (projectId: string) => {
    try {
      // Convert string ID to number so we can use it with the API cuz api expects number we might 
      //implement better error handling later
      const numericId = Number.parseInt(projectId, 10);
      
      if (Number.isNaN(numericId)) {
        console.error('Invalid project ID:', projectId);
        showError('Invalid Project ID', 'Cannot delete project with invalid ID');
        return;
      }
      
      
      // Delete the project
      await deleteProjectMutation.mutateAsync({
        id: numericId
      });
      
      showSuccess('Project Deleted', 'Project has been successfully deleted');
      
      // Invalidate and refetch projects
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      
    } catch (error) {
      console.error('Failed to delete project:', error);
      showError('Failed to Delete Project', getApiErrorMessage(error));
    }
  };

    const handleProjectTemplateCreate = (project: ProjectListProject) => {
        setTemplateSourceProject({
            id: project.id,
            title: project.title,
            description: project.description,
        });
    };

  const handleViewChange = (view: ViewType) => {
    setCurrentView(view);
  };

  return (
    <div className={styles.dashboardLayout}> 
      <Sidebar />
      <div className={styles.dashboardContent}>
        <PageHeader title={t('title')}>
          <button type="button" className={styles.tutorialButton} onClick={() => setIsTutorialOpen(true)}>
            <IconPlayerPlay size={16} />
            <span>{t('tutorialCard.firstSteps')}</span>
          </button>
          <LanguageSwitcher />
          {isSystemAdmin ? <SystemAdminBadge /> : <NotificationBell />}
          {canCreateProject && (
            <button className={styles.addButton} onClick={() => setIsCreateProjectOpen(true)}>
            <IconPlus />
              <span className={styles.buttonText}>{t('addNewProject')}</span>
            </button>
          )}
        </PageHeader>
        <PortfolioTiles summary={portfolioBand} computedAt={portfolioComputedAt} />
        <ProjectListHeader currentView={currentView} onViewChange={handleViewChange} />
        <div className={`${styles.projectList} ${styles[currentView]}`}>
          {isLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: '#6b7280'
            }}>
              {t('loading')}
            </div>
          ) : isError ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: '#ef4444'
            }}>
              {t('errorLoading', { error: error?.message || 'Unknown error' })}
            </div>
          ) : uiProjects.length > 0 ? (
            <ProjectList 
              projects={uiProjects} 
              viewType={currentView} 
              onProjectClick={handleProjectClick}
              projectHref={(project) => `/project/${project.id}?tab=overview`}
              onProjectDelete={handleProjectDelete}
              onProjectTemplateCreate={!canSaveTemplates ? undefined : handleProjectTemplateCreate}
            />
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '2px dashed #e5e7eb'
            }}>
              <h3 style={{ marginBottom: '16px', color: '#374151' }}>{t('emptyState.title')}</h3>
              <p style={{ marginBottom: '24px' }}>{t('emptyState.description')}</p>
              {canCreateProject && (
                <button
                  onClick={() => setIsCreateProjectOpen(true)}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {t('emptyState.cta')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <TutorialDialog isOpen={isTutorialOpen} onClose={() => setIsTutorialOpen(false)} />
      {isCreateProjectOpen && (
        <CreateProjectPopup
          onClose={() => setIsCreateProjectOpen(false)}
          onSuccess={(project) => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setIsCreateProjectOpen(false);
            // Clear stale localStorage entry so the new project always opens on Cockpit
            if (project.id) {
              localStorage.removeItem(`projectActiveSection_${project.id}`);
            }
          }}
        />
      )}
        {templateSourceProject && (
            <CreateTemplatePopup
                initialProject={templateSourceProject}
                onClose={() => setTemplateSourceProject(null)}
                onSuccess={() => {
                    queryClient.invalidateQueries({queryKey: ['/template']});
                    queryClient.invalidateQueries({queryKey: ['projects']});
                    setTemplateSourceProject(null);
                }}
            />
        )}
    </div>
  );
};

export default DashboardPage; 
